<template>
  <BHeightAdjuster>
    <BModalBody>
      <div class="setting-message">
        {{ $t('productCategory.settingMessage') }}
      </div>
      <div
        v-loading="loading"
        class="list-content"
      >
        <div class="item-content">
          <BEmptyBox
            v-if="!loading && productCategories.length === 0"
            class="pt-600 pb-600"
          >
            {{ $t('productCategory.empty') }}
          </BEmptyBox>
          <div
            v-for="category in productCategories"
            :key="category.id"
            class="category-item"
          >
            <BLayout
              class="category-row py-100"
              align-center
              justify-space-between
            >
              <BEditableContent
                class="category-name"
                :value="category.name"
                :placeholder="$t('productCategory.name')"
                :rules="[requiredRule]"
                :loading="updatingCategoryId === category.id"
                :uneditable="isApiWaiting"
                @check="handleUpdateCategory($event, category.id)"
              />
              <BMoreMenu>
                <BBtn
                  flat
                  fit
                  size="small"
                  :loading="deletingCategoryId === category.id"
                  :disabled="isApiWaiting"
                  @click="() => handleDeleteCategory(category)"
                >
                  {{ $t('general.delete.do') }}
                </BBtn>
              </BMoreMenu>
            </BLayout>
            <div class="products-list">
              <BLayout
                v-for="product in category.products"
                :key="product.id"
                class="product-row py-100"
                align-center
                justify-space-between
              >
                <BEditableContent
                  :value="product.name"
                  :placeholder="$t('product.name')"
                  :rules="[requiredRule]"
                  :loading="updatingProductId === product.id"
                  :uneditable="isApiWaiting"
                  @check="handleUpdateProduct($event, category.id, product.id)"
                />
                <BBtn
                  text
                  size="small"
                  :loading="deletingProductId === product.id"
                  :disabled="isApiWaiting"
                  @click="() => handleDeleteProduct(category.id, product)"
                >
                  <BIcon
                    class="px-200"
                    size="small"
                    type="gray"
                  >
                    close
                  </BIcon>
                </BBtn>
              </BLayout>
              <BInputBox
                v-if="addingProductCategoryId === category.id"
                v-model="newProductName"
                class="add-product-input"
                horiz
                required
                :loading="creatingProduct"
                :placeholder="$t('product.name')"
                @check="() => handleCreateProduct(category.id)"
                @close="closeAddProductForm"
              />
              <BBtn
                v-else
                type="primary"
                text
                size="small"
                :disabled="isApiWaiting"
                @click="() => openAddProductForm(category.id)"
              >
                <span class="add-product-btn">{{ $t('product.add') }}</span>
              </BBtn>
            </div>
          </div>
        </div>
      </div>
    </BModalBody>
    <BModalFooter>
      <BLayout
        class="footer"
        align-center
        justify-center
      >
        <BInput
          v-model="newCategoryName"
          v-model:valid="newCategoryValid"
          class="input-name mr-400"
          :placeholder="$t('productCategory.name')"
          :rules="[requiredRule]"
          :disabled="isApiWaiting"
          @keypress-enter="handleCreateCategory"
        />
        <BBtn
          type="primary"
          :disabled="!newCategoryValid || isApiWaiting"
          :loading="creatingCategory"
          @click="handleCreateCategory"
        >
          {{ $t('general.append.text') }}
        </BBtn>
      </BLayout>
    </BModalFooter>
  </BHeightAdjuster>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Product, ProductCategory } from '@/api/openapi';
import { ProductCategoryApiService } from '@/api/user/resources/product_category';
import { useInputValidations } from '@/composable/input-validations';
import { useAlert, useConfirm } from '@/composable/message-dialog';

const { requiredRule } = useInputValidations();
const i18n = useI18n();
const { confirm } = useConfirm();
const { alert } = useAlert();
const api = new ProductCategoryApiService();

const productCategories = ref<ProductCategory[]>([]);
const loading = ref(false);
const creatingCategory = ref(false);
const creatingProduct = ref(false);
const updatingCategoryId = ref<number | null>(null);
const updatingProductId = ref<number | null>(null);
const deletingCategoryId = ref<number | null>(null);
const deletingProductId = ref<number | null>(null);

const newCategoryName = ref('');
const newCategoryValid = ref(false);

const addingProductCategoryId = ref<number | null>(null);
const newProductName = ref('');

const isApiWaiting = computed(
  () =>
    deletingCategoryId.value !== null
    || deletingProductId.value !== null
    || updatingCategoryId.value !== null
    || updatingProductId.value !== null
    || creatingCategory.value
    || creatingProduct.value,
);

const load = async () => {
  loading.value = true;
  try {
    const result = await api.getProductCategories();
    productCategories.value = result.data.productCategories;
  } finally {
    loading.value = false;
  }
};

const handleCreateCategory = async () => {
  if (!newCategoryValid.value) return;
  creatingCategory.value = true;
  try {
    await api.postProductCategory({
      postProductCategoryRequest: { productCategory: { name: newCategoryName.value } },
    });
    // バリデーションエラー時はフォームを残して修正できるようにするため、成功時のみリセット
    newCategoryName.value = '';
    await load();
  } finally {
    creatingCategory.value = false;
  }
};

const handleUpdateCategory = async (name: string, categoryId: number) => {
  updatingCategoryId.value = categoryId;
  try {
    await api.putProductCategory({
      productCategoryId: categoryId,
      postProductCategoryRequest: { productCategory: { name } },
    });
    await load();
  } finally {
    updatingCategoryId.value = null;
  }
};

const handleDeleteCategory = async (category: ProductCategory) => {
  const ok = await confirm({
    title: i18n.t('general.confirm.text'),
    message: i18n.t('productCategory.deleteConfirmMessage', { name: category.name }),
  });
  if (!ok) return;
  deletingCategoryId.value = category.id;
  try {
    try {
      await api.deleteProductCategory({ productCategoryId: category.id });
    } catch {
      await alert({
        title: i18n.t('general.error'),
        message: i18n.t('general.fail.to', { action: i18n.t('general.delete.text') }),
      });
      return;
    }
    if (addingProductCategoryId.value === category.id) {
      closeAddProductForm();
    }
    await load();
  } finally {
    deletingCategoryId.value = null;
  }
};

const openAddProductForm = (categoryId: number) => {
  addingProductCategoryId.value = categoryId;
  newProductName.value = '';
};

const closeAddProductForm = () => {
  addingProductCategoryId.value = null;
  newProductName.value = '';
};

const handleCreateProduct = async (categoryId: number) => {
  if (!newProductName.value) return;
  creatingProduct.value = true;
  try {
    await api.postProduct({
      productCategoryId: categoryId,
      postProductRequest: { product: { name: newProductName.value } },
    });
    // バリデーションエラー時はフォームを残して修正できるようにするため、成功時のみ閉じる
    closeAddProductForm();
    await load();
  } finally {
    creatingProduct.value = false;
  }
};

const handleUpdateProduct = async (name: string, categoryId: number, productId: number) => {
  updatingProductId.value = productId;
  try {
    await api.putProduct({
      productCategoryId: categoryId,
      productId,
      putProductRequest: { product: { name } },
    });
    await load();
  } finally {
    updatingProductId.value = null;
  }
};

const handleDeleteProduct = async (categoryId: number, product: Product) => {
  const ok = await confirm({
    title: i18n.t('general.confirm.text'),
    message: i18n.t('product.deleteConfirmMessage', { name: product.name }),
  });
  if (!ok) return;
  deletingProductId.value = product.id;
  try {
    try {
      await api.deleteProduct({ productCategoryId: categoryId, productId: product.id });
    } catch {
      await alert({
        title: i18n.t('general.error'),
        message: i18n.t('general.fail.to', { action: i18n.t('general.delete.text') }),
      });
      return;
    }
    await load();
  } finally {
    deletingProductId.value = null;
  }
};

onMounted(async () => {
  await load();
});
</script>

<style lang="scss" scoped>
:deep(.b-modal-body) {
  margin-top: 20px;
  padding: 0 32px 20px;
  .list-content {
    .item-content {
      height: calc(100vh - 360px);
      overflow-y: auto;
    }
  }
}

.setting-message {
  color: $textcolor-light;
  margin-bottom: $basespace-400;
}

.category-item {
  & + .category-item {
    margin-top: $basespace-400;
  }
}

.category-row {
  @media only screen and (min-width: 959px) {
    width: 50%;
  }
  .category-name {
    padding-left: $basespace-100;
    font-weight: bold;
  }
}

.products-list {
  padding-left: $basespace-600;

  .product-row,
  .add-product-input {
    @media only screen and (min-width: 959px) {
      width: calc(50% - #{$basespace-600});
    }
  }

  .product-row {
    border-bottom: 1px solid $bdcolor-light;
  }

  .add-product-btn {
    color: $basecolor-primary;
  }
}

.footer {
  padding-top: $basespace-300;
  .input-name {
    width: $setting-input-width;
  }
}
</style>
